import { useEffect } from 'react';
import { log } from 'log';
import { pick } from 'ramda';
import { useDispatch } from 'react-redux';
import { useGetBingoJackpots } from 'server';
import { useAppSelector, useUserSelector } from 'js/store/hooks';

import * as server from './server';
import * as helpers from './helpers';
import * as actions from './store/actions';

export const useGetJackpots = () => {
    const dispatch = useDispatch();
    const getBingoJackpots = useGetBingoJackpots();
    const { country, currencyCode } = useUserSelector(pick(['country', 'currencyCode']));
    const {
        jackpotsCasinoUrl, jackpotsCasino, useServicesCasinoJackpots,
        locationJson, casinoName, useServicesVfJackpots, defaultBingoRegion,
        externalJackpotsCasino, externalJackpotsCasinoUrl, externalJackpotsGameProviders
    } = useAppSelector(pick([
        'jackpotsCasinoUrl', 'jackpotsCasino', 'useServicesCasinoJackpots',
        'locationJson', 'casinoName', 'useServicesVfJackpots', 'defaultBingoRegion',
        'externalJackpotsCasino', 'externalJackpotsCasinoUrl', 'externalJackpotsGameProviders'
    ]));

    useEffect(() => {
        dispatch(actions.clearJackpots());
    }, [dispatch, currencyCode]);

    useEffect(() => {
        if (jackpotsCasinoUrl && jackpotsCasino && currencyCode && useServicesCasinoJackpots) {
            (async () => {
                try {
                    const { jackpots, casinoJackpot } = await server.getJackpots({
                        jackpotsCasinoUrl, jackpotsCasino, currencyCode
                    });

                    dispatch(actions.setJackpots(jackpots));
                    dispatch(actions.setTotalCasinoJackpot(casinoJackpot));
                } catch (error) {
                    log('error', '[Jackpots] unable to get casino jackpots', error);
                }
            })();
        }
    }, [useServicesCasinoJackpots, jackpotsCasinoUrl, jackpotsCasino, currencyCode, dispatch]);

    useEffect(() => {
        if (!currencyCode || !casinoName || !useServicesVfJackpots) {
            return;
        }

        const items = locationJson ? locationJson.items : [];
        const regionItem = items?.find(location => location.currency === currencyCode && location.countryCode === country);
        const region = regionItem?.region || defaultBingoRegion;

        if (!region) {
            return;
        }

        (async () => {
            try {
                const response = await getBingoJackpots({ region });
                const bingoJackpots = helpers.parseVFJackpots(response.jackpots);

                dispatch(actions.setJackpots(bingoJackpots));
            } catch (error) {
                log('error', '[Jackpots] unable to get bingo jackpots', error);
            }
        })();
    }, [
        country,
        dispatch,
        casinoName,
        currencyCode,
        locationJson,
        getBingoJackpots,
        defaultBingoRegion,
        useServicesVfJackpots
    ]);

    useEffect(() => {
        if (externalJackpotsCasino && externalJackpotsCasinoUrl && currencyCode) {
            (async () => {
                try {
                    const jackpots = await server.getExternalJackpots({
                        currencyCode,
                        externalJackpotsCasino,
                        externalJackpotsCasinoUrl,
                        rgsId: externalJackpotsGameProviders
                    });

                    dispatch(actions.setJackpots(jackpots));
                } catch (error) {
                    log('error', '[Jackpots] unable to get external jackpots', error);
                }
            })();
        }
    }, [
        currencyCode,
        dispatch,
        externalJackpotsCasino,
        externalJackpotsCasinoUrl,
        externalJackpotsGameProviders
    ]);
};
